
import BalancaCheckoutService, {
  DEFAULT_CONFIG,
} from "@/modules/PdvDesktop/services/BalancaCheckoutService";

export default {
  name: "ConfiguracaoBalanca",
  data() {
    return {
      form: { ...DEFAULT_CONFIG },
      modelos: [],
      portas: [],
      diagnostico: {},
      carregando: true,
      salvando: false,
      detectando: false,
      testando: false,
      mensagem: "",
      erro: false,
      removerListener: null,
    };
  },
  computed: {
    ocupado() {
      return this.carregando || this.salvando || this.detectando || this.testando;
    },
    portaSalvaNaoListada() {
      return Boolean(
        this.form.portaSerial &&
          !this.portas.some((porta) => porta.path === this.form.portaSerial)
      );
    },
    status() {
      if (!this.form.habilitada) {
        return {
          tipo: "inativo",
          icone: "fas fa-circle",
          texto: "Desativada",
        };
      }
      if (this.diagnostico.conectado) {
        return {
          tipo: "ok",
          icone: "fas fa-check-circle",
          texto: "Balança pronta",
        };
      }
      if (this.diagnostico.reconectando) {
        return {
          tipo: "alerta",
          icone: "fas fa-sync-alt fa-spin",
          texto: "Reconectando...",
        };
      }
      return {
        tipo: "alerta",
        icone: "fas fa-exclamation-circle",
        texto: "Não conectada",
      };
    },
    pesoFormatado() {
      const peso = this.diagnostico?.ultimaAmostra?.peso;
      if (!Number.isFinite(Number(peso))) return "—";
      return `${Number(peso).toLocaleString("pt-BR", {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      })} kg`;
    },
  },
  async mounted() {
    this.removerListener = BalancaCheckoutService.onEvento((payload) => {
      if (payload.tipo === "conexao") {
        this.$set(this.diagnostico, "conectado", Boolean(payload.conectado));
        this.$set(
          this.diagnostico,
          "reconectando",
          Boolean(payload.reconectando && !payload.conectado)
        );
        if (payload.porta) this.$set(this.diagnostico, "porta", payload.porta);
        if (payload.adaptadorId) {
          this.$set(this.diagnostico, "adaptadorId", payload.adaptadorId);
        }
      }
      if (payload.tipo === "peso_tempo_real" || payload.tipo === "peso_estavel") {
        this.$set(this.diagnostico, "ultimaAmostra", payload);
      }
      if (payload.tipo === "erro") {
        this.$set(this.diagnostico, "ultimoErro", payload.mensagem);
      }
    });

    try {
      const [form, modelos] = await Promise.all([
        BalancaCheckoutService.carregarConfiguracao(),
        BalancaCheckoutService.listarModelos(),
      ]);
      this.form = form;
      this.modelos = modelos;
      await this.carregarPortas(false);
      if (this.form.habilitada && this.form.portaSerial) {
        try {
          this.diagnostico = await BalancaCheckoutService.conectar(this.form);
          await this.sincronizarIdentificacaoAdaptador();
        } catch (error) {
          this.diagnostico = await BalancaCheckoutService.diagnostico();
          this.erro = true;
          this.mensagem = error?.message || "Não foi possível abrir a porta serial.";
        }
      } else {
        this.diagnostico = await BalancaCheckoutService.diagnostico(this.form);
      }
    } catch (error) {
      this.erro = true;
      this.mensagem = error?.message || "Não foi possível carregar a configuração.";
    } finally {
      this.carregando = false;
    }
  },
  beforeDestroy() {
    if (typeof this.removerListener === "function") this.removerListener();
  },
  methods: {
    async carregarPortas(mostrarMensagem = true) {
      try {
        this.portas = await BalancaCheckoutService.listarPortas();
        if (mostrarMensagem) {
          this.erro = false;
          this.mensagem = this.portas.length
            ? `${this.portas.length} porta(s) serial(is) encontrada(s).`
            : "Nenhuma porta serial encontrada.";
        }
      } catch (error) {
        this.portas = [];
        if (mostrarMensagem) {
          this.erro = true;
          this.mensagem = error?.message || "Não foi possível listar as portas.";
        }
      }
    },
    aplicarPadraoModelo() {
      const modelo = this.modelos.find((item) => item.codigo === this.form.modelo);
      if (!modelo) return;
      this.form.baudRate = Number(modelo.baudDefault || 9600);
      this.form.casasDecimais = Number(modelo.casasDecimaisDefault || 3);
    },
    async detectar() {
      this.detectando = true;
      this.mensagem = "";
      try {
        this.form.habilitada = true;
        const resultado = await BalancaCheckoutService.detectar(this.form);
        this.form = {
          ...this.form,
          ...resultado.config,
          portaSerial: resultado.porta.path,
          baudRate: resultado.baudRate,
          habilitada: true,
        };
        this.form = await BalancaCheckoutService.salvarConfiguracao(this.form);
        await this.carregarPortas(false);
        this.diagnostico = await BalancaCheckoutService.diagnostico();
        this.erro = false;
        this.mensagem = `Balança detectada em ${resultado.porta.path} (${resultado.baudRate} baud).`;
      } catch (error) {
        this.erro = true;
        this.mensagem = error?.message || "A balança não foi detectada.";
        this.diagnostico = await BalancaCheckoutService.diagnostico();
      } finally {
        this.detectando = false;
      }
    },
    async testar() {
      this.testando = true;
      this.mensagem = "";
      try {
        await BalancaCheckoutService.conectar(this.form);
        const amostra = await BalancaCheckoutService.lerPeso(this.form);
        this.$set(this.diagnostico, "ultimaAmostra", amostra);
        this.diagnostico = await BalancaCheckoutService.diagnostico();
        if (amostra.status !== "estavel") {
          throw new Error(`A balança respondeu com peso ${amostra.status}.`);
        }
        this.erro = false;
        this.mensagem = `Leitura recebida: ${Number(amostra.peso).toLocaleString(
          "pt-BR",
          { minimumFractionDigits: 3, maximumFractionDigits: 3 }
        )} kg.`;
      } catch (error) {
        this.erro = true;
        this.mensagem = error?.message || "Não foi possível ler o peso.";
        this.diagnostico = await BalancaCheckoutService.diagnostico();
      } finally {
        this.testando = false;
      }
    },
    async salvar() {
      this.salvando = true;
      this.mensagem = "";
      try {
        if (this.form.habilitada && !this.form.portaSerial) {
          throw new Error("Selecione uma porta ou use a detecção automática.");
        }
        this.form = await BalancaCheckoutService.salvarConfiguracao(this.form);
        if (this.form.habilitada) {
          this.diagnostico = await BalancaCheckoutService.conectar(this.form);
          await this.sincronizarIdentificacaoAdaptador();
        } else {
          await BalancaCheckoutService.desconectar();
          this.diagnostico = await BalancaCheckoutService.diagnostico(this.form);
        }
        this.erro = false;
        this.mensagem = "Configuração da balança salva neste PDV Desktop.";
      } catch (error) {
        this.erro = true;
        this.mensagem = error?.message || "Não foi possível salvar a configuração.";
      } finally {
        this.salvando = false;
      }
    },
    async sincronizarIdentificacaoAdaptador() {
      if (
        !this.diagnostico.adaptadorId ||
        (this.form.adaptadorId === this.diagnostico.adaptadorId &&
          this.form.portaSerial === this.diagnostico.porta)
      ) {
        return;
      }
      this.form.adaptadorId = this.diagnostico.adaptadorId;
      this.form.portaSerial = this.diagnostico.porta;
      this.form = await BalancaCheckoutService.salvarConfiguracao(this.form);
    },
  },
};
